// Pure helpers for persisting an email-hash in `localStorage` so every
// subsequent tracking event on the same browser can attach an `em`
// signal to the Meta + TikTok CAPI dispatch. Without this signal the
// Event Match Quality is capped around 2-3 / 10 because no shared
// identity field links the visitor across PageView/ViewContent/
// InitiateCheckout/Purchase events.
//
// Casper 2026-05-15 P1b directive — "kun je bevestigen dat de email-hash
// in localStorage veilig (gehasht) wordt opgeslagen". YES: we hash with
// SHA-256 in the browser via `crypto.subtle.digest` BEFORE writing to
// storage. The plaintext email never lives in localStorage and never
// crosses the FE→BE network hop (we send the hex-hash as `emailHash`
// in the request body so the BE plugs it directly into `user_data.em`
// without re-hashing).
//
// Storage key: `favotrip_eh` (eh = email hash). Hash format: 64 lower
// hex chars (sha256 of the lowercased + trimmed email).

const STORAGE_KEY = "favotrip_eh";
const HEX64_PATTERN = /^[0-9a-f]{64}$/;

/**
 * Async SHA-256 of a string returning lowercase hex. Uses the supplied
 * `subtleLike.digest` if provided (test-injection point), otherwise the
 * global `crypto.subtle` (browser + node 18+).
 *
 * Returns `null` for falsy / non-string input so callers don't have to
 * pre-validate.
 *
 * @param {string} value
 * @param {{ digest?: (algo: string, data: ArrayBuffer) => Promise<ArrayBuffer> }} [subtleLike]
 * @returns {Promise<string|null>}
 */
export async function sha256Hex(value, subtleLike) {
  if (typeof value !== "string" || !value) return null;
  try {
    const enc = new TextEncoder().encode(value);
    const subtle =
      subtleLike ??
      (typeof crypto !== "undefined" && crypto.subtle ? crypto.subtle : null);
    if (!subtle || typeof subtle.digest !== "function") return null;
    const buf = await subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

/**
 * Normalise + hash an email address. Mirrors the server-side
 * normalisation in `meta-conversions.service.ts:193` and
 * `tiktok-events.service.ts:198` exactly so the produced hash matches
 * what the BE would have generated from raw input.
 *
 * @param {string} email
 * @param {{ digest?: Function }} [subtleLike]
 * @returns {Promise<string|null>}
 */
export async function hashEmail(email, subtleLike) {
  if (typeof email !== "string") return null;
  const norm = email.trim().toLowerCase();
  if (!norm) return null;
  return sha256Hex(norm, subtleLike);
}

/**
 * Validate an externally-supplied hex hash (defensive — rejects any
 * caller that tries to stick non-hex bytes in storage).
 *
 * @param {string|null|undefined} hash
 * @returns {boolean}
 */
export function isValidEmailHashHex(hash) {
  return typeof hash === "string" && HEX64_PATTERN.test(hash);
}

/**
 * Read the persisted email hash from a storage-like object (browser
 * `window.localStorage` or a Map-wrapping shim in tests). Returns
 * `undefined` when absent or invalid.
 *
 * @param {Pick<Storage, "getItem">} storage
 * @returns {string|undefined}
 */
export function readEmailHash(storage) {
  if (!storage || typeof storage.getItem !== "function") return undefined;
  let raw;
  try {
    raw = storage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
  return isValidEmailHashHex(raw) ? raw : undefined;
}

/**
 * Hash + persist an email. Returns the hash for callers that want to
 * also forward it to a same-tick fetch (cuts a localStorage round-trip).
 * No-op + returns null if storage is absent (server-side, locked-down
 * private-browsing, etc).
 *
 * @param {Pick<Storage, "setItem">} storage
 * @param {string} email raw email
 * @param {{ digest?: Function }} [subtleLike]
 * @returns {Promise<string|null>}
 */
export async function persistEmailHash(storage, email, subtleLike) {
  const hash = await hashEmail(email, subtleLike);
  if (!hash) return null;
  if (!storage || typeof storage.setItem !== "function") return hash;
  try {
    storage.setItem(STORAGE_KEY, hash);
  } catch {
    // private-browsing or quota-exceeded; swallow.
  }
  return hash;
}

/**
 * Browser convenience — hash + persist the email + return the hash.
 * No-op when running in a non-browser environment (returns null).
 *
 * @param {string} email
 * @returns {Promise<string|null>}
 */
export async function captureEmailInBrowser(email) {
  if (typeof window === "undefined") return null;
  return persistEmailHash(window.localStorage, email);
}

// Exposed for tests + cross-module reads.
export const __INTERNALS = { STORAGE_KEY, HEX64_PATTERN };
